import { useCallback, useRef, useState, useEffect } from "react"
import Webcam from "react-webcam"
import fixWebmDuration from "fix-webm-duration"
import moment from "moment"
import momentDurationFormatSetup from "moment-duration-format"
import intl from "react-intl-universal"

import RcmpModalAdd from "../../molecules/ModalAdd"
import styles from "./index.module.scss"
import TrashIcon from "../../../assets/icons/TrashIcon"
import CameraVideoIcon from "../../../assets/icons/CameraVideoIcon"
import StopIcon from "../../../assets/icons/StopIcon"
import RcmpLoading from "../../atoms/Loading"
import Label from "../../atoms/Label"
import RefreshIcon from "../../../assets/icons/RefreshIcon"

const nMaxVideoRecord = 120 // 2 min;

export default function RcmpModalVideoCapture(props) {
  const {
    bIsOpen,
    fnOnClose,
    fnSubmitVideoEvidence,
    cFileName,
    setValue,
    fnSwitchCamera,
    cVideoMode,
    bCanSwitchCamera
  } = props

  const [bLoadingCam, setFnBLoadingCam] = useState(true)
  const [capturing, setCapturing] = useState(false)
  const [recordedChunks, setRecordedChunks] = useState([])
  const [blobVideo, setBlobVideo] = useState(null)
  const [nCounter, setFnNCounter] = useState(0) // in seconds
  const [cCamError, setFncCamError] = useState(null)
  const [cAudioState, setFncAudioState] = useState(null)

  const camRef = useRef(null)
  const mediaRecorderRef = useRef(null)

  momentDurationFormatSetup(moment)

  useEffect(() => {
    if (!bIsOpen) return

    navigator.mediaDevices.getUserMedia({ audio: true }).then(() => {
      navigator.permissions.query({ name: "microphone" }).then((oPermissionStatus) => {
        setFncAudioState(oPermissionStatus?.state)
      })
    }).catch((e) => {
      setFncAudioState(e)
    })
  }, [bIsOpen])

  const handleStartCaptureClick = useCallback(() => {
    setCapturing(true)
    mediaRecorderRef.current = new MediaRecorder(camRef.current.stream, {
      mimeType: "video/webm",
    })
    mediaRecorderRef.current.addEventListener(
      "dataavailable",
      handleDataAvailable
    )
    mediaRecorderRef.current.start()
  }, [camRef, setCapturing, mediaRecorderRef])

  const handleDataAvailable = useCallback(
    ({ data }) => {
      if (data.size > 0) {
        setRecordedChunks((prev) => prev.concat(data))
      }
    },
    [setRecordedChunks]
  )

  async function dataUrlToFile(dataUrl) {
    const res = await fetch(dataUrl)
    const blob = await res.blob()
    return new File([blob], cFileName, { type: "video/mp4" })
  }

  const handleStopCaptureClick = useCallback(async () => {
    mediaRecorderRef.current.stop()
    setCapturing(false)
  }, [mediaRecorderRef, camRef, setCapturing])

  function fnRemoveVideo() {
    setCapturing(false)
    setRecordedChunks([])
    setBlobVideo(null)
    setFnNCounter(0)
    setFnBLoadingCam(true)
  }

  function fnHandleOnCamLoad() {
    setFnBLoadingCam(false)
  }

  function fnHandleOnClose() {
    fnRemoveVideo()
    fnOnClose()
  }

  useEffect(() => {
    if (!capturing) return

    const timeIncrement = setTimeout(() => {
      if (nCounter <= nMaxVideoRecord) setFnNCounter(nCounter + 1)
    }, 1000)

    if (nCounter >= nMaxVideoRecord + 1) {
      clearTimeout(timeIncrement)
      handleStopCaptureClick()
      setFnNCounter(nMaxVideoRecord)
      return
    }

    return () => clearTimeout(timeIncrement)
  }, [nCounter, capturing])

  useEffect(() => {
    if (capturing || !recordedChunks?.length || nCounter <= 0) return

    const blob = new Blob(recordedChunks, {
      type: "video/mp4",
    })

    const nCounterMilliseconds = (nCounter - 1) * 1000

    // set duration metadada
    fixWebmDuration(blob, nCounterMilliseconds, function (fixedBlob) {
      const url = URL.createObjectURL(fixedBlob)
      setBlobVideo(url)
    })
  }, [capturing, recordedChunks, nCounter])

  if (!bIsOpen) return null

  async function fnConfirmSubmitVideoEvince() {
    const response = await dataUrlToFile(blobVideo)

    setValue("opae_cvalue", response)
    fnSubmitVideoEvidence()
    fnRemoveVideo()
  }

  function fnHandleOnCamLoadError() {
    setFncCamError("Ocorreu um erro para carregar a câmera")
  }

  const oVideoConstraints = {
    width: { min: 461, ideal: 1280, max: 1280 },
    height: { min: 284, ideal: 720, max: 720 },
    video: { frameRate: 30 },
    facingMode: cVideoMode
  }

  const nFormatedCounter = moment
    .duration(nCounter, "seconds")
    .format("mm:ss", { trim: false })

  const bHasAudio = cAudioState === "granted"

  return (
    <RcmpModalAdd
      isOpen={bIsOpen}
      buttonCancelText={intl.get("cancel")}
      buttonSaveText={intl.get("save")}
      title={intl.get("ActivityEvidence.record_video")}
      fnHandleCancel={fnHandleOnClose}
      fnHandleConfirm={fnConfirmSubmitVideoEvince}
      buttonType={"button"}
      size={"md"}
      disabledSaveButton={blobVideo ? false : true}
      cAlertMessage={cCamError}
    >
      <div className={styles.contentContainer}>
        {bLoadingCam && (
          <div className={styles.loadingCam}>
            <RcmpLoading bShow={true} />
          </div>
        )}
        {(capturing || !blobVideo) && (
          <>
            <div className={styles.renderContainer}>
              <Label className={styles.counter}>{nFormatedCounter}</Label>
              {cAudioState !== null && (
                <Webcam
                  ref={camRef}
                  audio={bHasAudio}
                  muted={true}
                  height={284}
                  width={461}
                  className={styles.cameraPreview}
                  onUserMedia={fnHandleOnCamLoad}
                  onUserMediaError={fnHandleOnCamLoadError}
                  videoConstraints={oVideoConstraints}
                />
              )}
            </div>
            <div className={styles.captureButton}>
              {!capturing ? (
                <>
                  <button type="button" onClick={handleStartCaptureClick}>
                    <CameraVideoIcon fill={"#6CB6E3"} />
                  </button>
                  {bCanSwitchCamera && (
                    <button onClick={fnSwitchCamera}>
                      <RefreshIcon fill={"#6CB6E3"} />
                    </button>
                  )}
                </>
              ) : (
                <button type="button" onClick={handleStopCaptureClick}>
                  <StopIcon fill={"#6CB6E3"} />
                </button>
              )}
            </div>
            <p className={styles.timeLimitMessage}>Tempo limite: 2 min.</p>
          </>
        )}
        {!capturing && blobVideo && (
          <div className={styles.playVideo}>
            <video
              src={blobVideo}
              height="284px"
              width="461px"
              type="video/mp4"
              controls
              disablePictureInPicture
              controlsList="nodownload noplaybackrate"
            />
            <div className={styles.playButtons}>
              <button type="button">
                <TrashIcon fill={"#6CB6E3"} onClick={fnRemoveVideo} />
              </button>
            </div>
          </div>
        )}
      </div>
    </RcmpModalAdd>
  )
}
